/**
 * The Template Version definition.
 *
 * This is the structure a Planning Workspace is instantiated from: sections, items, the options each
 * item offers, its defaults, its constraints, and the role roster. It is stored as a single `jsonb`
 * column and is **immutable once the version is published**.
 *
 * It lives in `@pius/contracts` because both sides read it: the backend instantiates a Workspace
 * from it and validates submissions against it; the frontend renders the authoring surface and the
 * planning surface from the same shape. A change here breaks compilation on both sides, which is
 * the point.
 *
 * **A Template must preserve every option the Rite permits.** It cannot remove a permitted reading
 * because someone prefers fewer — narrowing what is *offered* is Selection Policy's job, and only
 * Selection Policy's (docs/engineering/01-domain-model.md).
 *
 * See docs/engineering/06-selection-policy.md
 */

import type { ItemType, ItemValue } from './item-types.js';
import type { LaunchLanguage } from './language.js';

/**
 * A stable key within a Template Version.
 *
 * Keys are **persisted into every Liturgy** instantiated from the version, and a Liturgy keeps the
 * version it originated from. Renaming a key in a new version therefore does not rewrite history —
 * but reusing an old key for a different meaning would silently reinterpret it. Add, never repurpose.
 */
export type TemplateKey = string;

export interface TemplateOption {
  key: TemplateKey;
  /** Presentation text in the version's primary language; localizations override it. */
  label: string;
  /** Ordering within the option set. Ties fall back to definition order. */
  ordinal: number;
  /** Reference data pointer — a lectionary key, a hymnal number. */
  source_ref?: string;
  /**
   * Retired options remain so historical Liturgies still resolve them, but they are not offered on
   * new ones. Removing an option outright would orphan approved selections that reference it.
   */
  retired?: boolean;
}

/**
 * What makes an Item required, and on whose authority.
 *
 * **Kind and source are separate axes**, which the mockup's Constraints pane makes explicit:
 *
 *   Date and time must be present before Finalization  · Required field      · This Template
 *   Nothing may be placed on the mensa of the altar     · Local Custom        · St Pius X Church
 *   Marriage within and outside Mass are exclusive      · Mutually exclusive  · Order of Celebrating Matrimony
 *   Readings must come from the Marriage Lectionary     · Jurisdictional norm · Order of Celebrating Matrimony
 *
 * `kind` says what sort of rule it is. `source` names the authority it comes from, and is what
 * makes an inherited constraint visibly not the local Template's to remove — a parish can drop its
 * own Local Custom, but not a norm of the Rite.
 */
export const CONSTRAINT_KINDS = [
  /** Simply required before Finalization. */
  'required_field',
  /** A parish practice — the kind a local editor may legitimately relax. */
  'local_custom',
  /** Two or more options that cannot both be chosen. */
  'mutually_exclusive',
  /** A norm of the Rite or the diocese. Not the parish's to remove. */
  'jurisdictional_norm',
] as const;
export type ConstraintKind = (typeof CONSTRAINT_KINDS)[number];

export interface TemplateConstraint {
  kind: ConstraintKind;
  /**
   * The authority this constraint comes from, as displayed: "This Template", "St Pius X Church",
   * "Order of Celebrating Matrimony". A human-readable name rather than an id, because the
   * authority is often a Rite or a diocese, which are not resources this system holds.
   */
  source: string;
  /** Shown when the constraint blocks Finalization. Explains, never scolds. */
  explanation: string;
}

export interface TemplateItem {
  key: TemplateKey;
  label: string;
  item_type: ItemType;
  ordinal: number;

  /** Guidance shown beneath the field on the planning surface. */
  help?: string;

  /**
   * The full Rite-permitted set for option-bearing types. Selection Policy narrows what is
   * *offered* from this; it never adds to it, and this list is the outer bound.
   */
  options?: TemplateOption[];

  /** Pre-filled on instantiation. A default is a suggestion, never an approval. */
  default_value?: ItemValue;

  /**
   * Present means the Item is required. **Only a constraint blocks Finalization** — an unset
   * optional Item is recorded as unset and carried into the export as such
   * (docs/engineering/03-lifecycle-and-states.md).
   */
  constraint?: TemplateConstraint;

  /** For `multi_option`. Ignored for every other type. */
  min_selections?: number;
  max_selections?: number;
}

export interface TemplateSection {
  key: TemplateKey;
  label: string;
  ordinal: number;
  /** Shown at the head of the section on the planning surface. */
  description?: string;
  items: TemplateItem[];
}

/**
 * A role in the roster — who serves, not who may act.
 *
 * **A Person assignment is not a permission.** Filling a role records service; authority comes only
 * from a Capability or an Ownership/Delegation relationship (docs/engineering/04-authorization.md).
 */
export interface TemplateRole {
  key: TemplateKey;
  label: string;
  ordinal: number;
  /**
   * A constraint role must be filled before Finalization. A template role is offered by default;
   * a local role is added on the Liturgy itself and is the only kind that may be withdrawn freely
   * (docs/engineering/11-screens.md).
   */
  required?: boolean;
  /** Several people may serve the same role — readers, servers, ministers of communion. */
  allows_multiple?: boolean;
}

export interface TemplateDefinition {
  /**
   * The language the labels above are written in. Untranslated participant-facing text falls back
   * here, never to a raw key — a participant must never see `planning.item.first_reading`
   * (docs/engineering/12-localization.md).
   */
  primary_language: LaunchLanguage;

  sections: TemplateSection[];
  roles: TemplateRole[];

  /**
   * Enabling the Extraordinary Form is a **Template** decision, never a per-Liturgy one. Roles
   * scoped to an Extraordinary Form Template cannot appear in an Ordinary Form Liturgy, and no
   * Selection Policy can offer them there (docs/engineering/01-domain-model.md).
   */
  extraordinary_form?: boolean;
}

/** Presentation-only overrides. Structure, defaults, and constraints are shared across languages. */
export interface TemplateLocalization {
  language_code: LaunchLanguage;
  /** Keyed by section, item, option, or role key. Missing keys fall back to the primary language. */
  messages: Record<TemplateKey, string>;
}

// ---------------------------------------------------------------------------
// invariants
// ---------------------------------------------------------------------------

export const TEMPLATE_VISIBILITIES = ['private', 'organization', 'public'] as const;
export type TemplateVisibility = (typeof TEMPLATE_VISIBILITIES)[number];

/** Only a Site Administrator may designate these, and it changes discoverability only. */
export const TEMPLATE_DESIGNATIONS = ['official', 'featured'] as const;
export type TemplateDesignation = (typeof TEMPLATE_DESIGNATIONS)[number];

/**
 * Every key that must be unique within a definition, flattened.
 *
 * Duplicate keys are the failure mode that matters: a Planning Workspace is instantiated by key, so
 * two items sharing one would collapse into a single field and silently discard a selection.
 */
export function collectKeys(definition: TemplateDefinition): string[] {
  const keys: string[] = [];

  for (const section of definition.sections) {
    keys.push(`section:${section.key}`);
    for (const item of section.items) {
      keys.push(`item:${item.key}`);
      for (const option of item.options ?? []) {
        // Option keys need only be unique within their item.
        keys.push(`option:${item.key}:${option.key}`);
      }
    }
  }

  for (const role of definition.roles) {
    keys.push(`role:${role.key}`);
  }

  return keys;
}

export function findDuplicateKeys(definition: TemplateDefinition): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const key of collectKeys(definition)) {
    if (seen.has(key)) duplicates.add(key);
    seen.add(key);
  }

  return [...duplicates];
}

/** Items whose absence blocks Finalization. */
export function constrainedItems(definition: TemplateDefinition): TemplateItem[] {
  return definition.sections.flatMap((s) => s.items.filter((i) => i.constraint !== undefined));
}
